import { Modal, Button, Group, TextInput, Select, Stack, Text, Checkbox } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconMail, IconUserPlus } from '@tabler/icons-react';
import { useCustomRoles } from '@/hooks/api/useCustomRoles';
import { useOrganization } from '@/hooks/useOrganization';

interface InviteUserModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (email: string, isAdmin: boolean, customRoleId: string | null) => Promise<void>;
  isLoading?: boolean;
}

export function InviteUserModal({ opened, onClose, onSubmit, isLoading }: InviteUserModalProps) {
  const { organizationId } = useOrganization();
  const { data: customRoles = [] } = useCustomRoles(organizationId || '');

  const form = useForm({
    initialValues: {
      email: '',
      isAdmin: false,
      customRoleId: '',
    },
    validate: {
      email: (value) => {
        if (!value) return 'Email is required';
        if (!/^\S+@\S+$/.test(value)) return 'Invalid email address';
        return null;
      },
      customRoleId: (value, values) => {
        // Only require a role if not admin
        if (!values.isAdmin && !value) return 'Custom role is required for non-admin users';
        return null;
      },
    },
  });

  const handleSubmit = async (values: { email: string; isAdmin: boolean; customRoleId: string }) => {
    await onSubmit(values.email, values.isAdmin, values.isAdmin ? null : values.customRoleId);
    form.reset();
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal opened={opened} onClose={handleClose} title="Invite User" size="md">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Invite an existing user to join your organization. They must already have an account.
          </Text>

          <TextInput
            label="Email Address"
            placeholder="user@example.com"
            leftSection={<IconMail size={16} />}
            required
            {...form.getInputProps('email')}
          />

          <Checkbox
            label="Admin - Full access, bypasses all permissions"
            description="If checked, user has unrestricted access to all features"
            {...form.getInputProps('isAdmin', { type: 'checkbox' })}
          />

          {!form.values.isAdmin && (
            <Select
              label="Custom Role"
              placeholder="Select a role"
              data={customRoles.map((role) => ({
                value: role.id,
                label: role.name,
              }))}
              required
              disabled={customRoles.length === 0}
              {...form.getInputProps('customRoleId')}
            />
          )}

          {!form.values.isAdmin && customRoles.length === 0 && (
            <Text size="sm" c="red">
              No custom roles available. Please create a role first.
            </Text>
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" leftSection={<IconUserPlus size={16} />} loading={isLoading}>
              Invite User
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
